import { useState, type JSX } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router'
import { useAuthStore } from '../../store/authStore.js'
import PlatformHeader from '../platform/PlatformHeader.js'
import PlatformSidebar from '../platform/PlatformSidebar.js'
import type { NavSection } from '../platform/PlatformSidebar.js'

// Module-level stable empty array — used as the zustand selector fallback so
// the selector never returns a fresh array (which would cause render loops).
const EMPTY_PERMS: string[] = []

// Layout permission gate: ANY of the platform permissions grants access.
// Users with zero platform perms are redirected to /403.
const PLATFORM_PERMS = [
  'platform:tenants_manage',
  'platform:impersonate',
  'platform:key_rotation',
  'platform:audit_view',
  'platform:feature_flags_admin',
  'platform:incidents_admin',
  'platform:roles_manage',
  'platform:permissions_manage',
  'platform:rbac_manage',
] as const

export default function PlatformLayout(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const permissions = useAuthStore((s) => s.user?.permissions ?? EMPTY_PERMS)
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState<NavSection | null>(null)

  // Redirect unauthenticated users to platform login
  if (!user) return <Navigate to="/login/platform" replace />

  // Layout gate: authenticated but lacking platform permissions
  const hasAny = permissions.includes('*') || PLATFORM_PERMS.some((p) => permissions.includes(p))
  if (!hasAny) return <Navigate to="/403" replace />

  const handleSectionChange = (section: NavSection) => {
    setActiveSection(section)
    // Navigate to the first item in the section
    const firstItem = section.items[0]
    if (firstItem) {
      navigate(firstItem.to)
    }
  }

  return (
    <div className="h-screen w-full overflow-hidden bg-[#f8f9fa] flex flex-col">
      <div className="h-[60px] flex-shrink-0 z-50">
        <PlatformHeader />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="z-[100] flex-shrink-0 h-full">
          <PlatformSidebar
            activeSection={activeSection}
            onSectionChange={handleSectionChange}
          />
        </div>

        <div className="flex-1 min-w-0 overflow-y-auto">
          <main className="p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  )
}
